import { AuthenticatedHttp } from '@/http/axios';
import { isImpersonatingSession } from '@/auth/impersonationSession';
import { AppError } from '@/utils/api';
import { delay } from '@/utils/helpers';
import { format } from 'date-fns';

const formatFinancialDate = (value) => {
  if (!value) return undefined;

  if (typeof value === 'string') {
    return value.includes('T') ? value.split('T')[0] : value;
  }

  if (value instanceof Date) {
    return format(value, 'yyyy-MM-dd');
  }

  return format(new Date(value), 'yyyy-MM-dd');
};

const parseWithdrawalsResponse = (response) => {
  const { data } = response;

  if (Array.isArray(data)) {
    return {
      count: data.length,
      results: data,
    };
  }

  const results = data?.results || data?.movements || data?.body?.movements || [];
  const count = data?.count ?? data?.totalItems ?? data?.body?.totalItems ?? results.length;

  return {
    ...data,
    count,
    results,
  };
};

// `currency` e `type` so viajam quando informados: o extrato em real chama sem nenhum dos dois e
// depende de continuar recebendo a lista inteira. A tela de saques em dolar manda os dois
// (`USD` + `payoneer`) para filtrar pelo que ela de fato mostra — filtrar so por moeda passaria a
// misturar trilhos no dia em que existir um segundo saque internacional.
export const getWithdrawals = async ({ page, search, start_date, end_date, currency, type }) =>
  AuthenticatedHttp.get('/withdrawals/', {
    params: {
      page,
      search,
      createdAt__gte: formatFinancialDate(start_date),
      createdAt__lte: formatFinancialDate(end_date),
      ...(currency && { currency }),
      ...(type && { type }),
    },
  }).then(parseWithdrawalsResponse);

export const getBalance = async ({ currency } = {}) =>
  AuthenticatedHttp.get('/financial/balance/', {
    params: currency ? { currency } : undefined,
  }).then((response) => response.data);

export const createCurrencyConversionQuote = async ({
  originCurrency = 'USD',
  targetCurrency = 'BRL',
} = {}) =>
  AuthenticatedHttp.post('/financial/currency-conversion/quote/', {
    origin_currency: originCurrency,
    target_currency: targetCurrency,
  }).then((response) => response.data);

export const previewCurrencyConversion = async ({ amount }) =>
  AuthenticatedHttp.post('/financial/currency-conversion/preview/', { amount }).then(
    (response) => response.data
  );

export const requestCurrencyConversionOtp = async ({ amount }) =>
  AuthenticatedHttp.post('/financial/currency-conversion/otp/request/', { amount }).then(
    (response) => response.data
  );

export const verifyCurrencyConversionOtp = async ({ otpCode }) =>
  AuthenticatedHttp.post('/financial/currency-conversion/otp/verify/', { otp_code: otpCode }).then(
    (response) => response.data
  );

export const executeCurrencyConversion = async ({
  confirmationHash,
  amount,
  originCurrency = 'USD',
  targetCurrency = 'BRL',
}) =>
  AuthenticatedHttp.post(
    '/financial/currency-conversion/execute/',
    confirmationHash
      ? { confirmation_hash: confirmationHash }
      : { amount, origin_currency: originCurrency, target_currency: targetCurrency }
  ).then((response) => response.data);

// Saque em dolar via Payoneer. Caminho paralelo ao da conversao: nao converte moeda, debita
// `balance_usd` e cria um saque que o financeiro paga manualmente.
// O GET responde sempre um envelope — `{"account": {...}}` ou `{"account": null}` — e nao a conta
// na raiz. O envelope nao e enfeite: `Response(None)` do DRF nao vira o literal `null`, vira corpo
// vazio sem `Content-Type`, e o axios entrega `''`. Com a chave fixa, "sem conta" e um `null` de
// verdade nos dois lados. O front desembrulha aqui para o resto da tela continuar falando de conta,
// e nao de envelope.
export const getPayoneerAccount = async () =>
  AuthenticatedHttp.get('/financial/payoneer/account/').then(
    (response) => response.data?.account ?? null
  );

export const createPayoneerAccount = async ({ payoneerEmail, holderName }) =>
  AuthenticatedHttp.post('/financial/payoneer/account/', {
    payoneer_email: payoneerEmail,
    holder_name: holderName,
  }).then((response) => response.data);

export const verifyPayoneerAccountOtp = async ({ otpCode }) =>
  AuthenticatedHttp.post('/financial/payoneer/account/otp/verify/', { otp_code: otpCode }).then(
    (response) => response.data
  );

export const getPayoneerWithdrawConfig = async () =>
  AuthenticatedHttp.get('/financial/payoneer/withdraw/config/').then((response) => response.data);

// `requestId` identifica a **intencao** de saque, nao a tentativa HTTP: a mesma intencao repetida
// (retentativa depois de 502, duplo clique) chega com o mesmo uuid e o backend devolve o saque que
// ja criou, em vez de debitar de novo.
export const createPayoneerWithdrawal = async ({ amount, requestId }) =>
  AuthenticatedHttp.post('/financial/payoneer/withdraw/', {
    amount,
    request_id: requestId,
  }).then((response) => response.data);

// Erro morre no erro: a rejeicao sobe pro chamador. Resolver com o corpo do erro fazia o
// consumidor tratar payload invalido como sucesso.
export const getDataCompany = async (options = {}) => {
  if (isImpersonatingSession()) return null;

  const response = await AuthenticatedHttp.get('/financial/company/', options);

  const filenameRegex = /\/([^/]+)$/;

  const files = (response.data?.files || []).map((item) => ({
    ...item,
    path: item?.file || '',
    name: item?.file?.match(filenameRegex)?.[1] || '',
  }));

  return { ...response.data, files };
};

export const getKyc = async () =>
  AuthenticatedHttp.get('/financial/kyc/').then((response) => response.data);

export const doWithdrawal = async ({ amount, codeToken, account }) => {
  const params = {
    amount,
    ...(codeToken && { mfa_code: codeToken }),
    ...(account !== undefined && account !== null && { account }),
  };
  return AuthenticatedHttp.post('/financial/withdraw/', params)
    .then((response) => response.data)
    .catch((error) => {
      throw new AppError(error);
    });
};

export const createSellerWithdraw = async ({
  amount,
  pixKey,
  account,
  type = 'default',
  codeToken,
}) => {
  const response = await AuthenticatedHttp.post(
    '/financial/withdraw/',
    {
      amount,
      ...(type && { type }),
      ...(pixKey && { pixKey }),
      ...(account !== undefined && account !== null && { account }),
      ...(codeToken && { mfa_code: codeToken }),
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

export const anticipatePendingAmount = async ({ amount, codeToken }) => {
  const params = codeToken
    ? { amount, mfa_code: codeToken }
    : {
      amount,
    };
  return AuthenticatedHttp.post('/financial/anticipate/', params)
    .then((response) => response.data)
    .catch((error) => {
      throw new AppError(error);
    });
};

// Chave Pix do saque: fica no cakto-backend (`financial/urls.py:34`).
//
// O CBK-376 repontou isto para `/seller/pix/` no cakto-split, usando uma
// `REACT_APP_API_SCPAY` que nunca foi configurada. Sem ela o axios caía no
// `REACT_APP_API_URL` e batia em `https://api.cakto.com.br/api/seller/pix/` —
// que não existe: o `split_gateway` está no `INSTALLED_APPS` do backend, mas as
// urls dele nunca foram incluídas no `cakto/urls.py`. Resultado: 404 no GET e no
// POST, destino do saque nulo, e "Efetuar Saque"/"Chave Pix" sumindo da tela.
//
// Migrar para o split segue possível, mas depende de a `REACT_APP_API_SCPAY`
// existir no deploy e de o `api.scpay.com.br` aceitar chamada do navegador
// (CORS + o JWT do usuário, hoje ele é consumido servidor-a-servidor com API key).
export const getPixKey = async () =>
  AuthenticatedHttp.get('/financial/pix/').then((response) => response.data);

export const savePixKey = async ({ pix }) =>
  AuthenticatedHttp.post('/financial/pix/', { pix }).then((response) => response.data);

// TODO REMOVER CASO NÃO TENHA MAIS CADASTRO DE DADOS BANCÁRIOS
export const saveAccountData = async (data) =>
  AuthenticatedHttp.post('/financial/bank_account/', { ...data, legalName: 'teste' }).then(
    (response) => response.data
  );

export const getFee = async ({ countryId } = {}) =>
  AuthenticatedHttp.get('/financial/fee/', {
    params: countryId ? { country_id: countryId } : undefined,
  }).then((response) => response.data);

export const SERPRO_DEPENDENCY_FAILED_CODE = 'serpro_dependency_failed';

export const SERPRO_DEPENDENCY_DEFAULT_MESSAGE =
  'Serviço de validação de CPF temporariamente indisponível. Tente novamente em alguns instantes.';

export const isSerproDependencyError = (error) =>
  error?.response?.status === 424 ||
  error?.response?.data?.code === SERPRO_DEPENDENCY_FAILED_CODE;

export const getSerproDependencyErrorMessage = (error) =>
  error?.response?.data?.detail || SERPRO_DEPENDENCY_DEFAULT_MESSAGE;

// Sem `gateway`, o backend pede a taxa a conta dona do preco do pais da moeda (CKTPL-1373), o
// mesmo criterio do checkout. A conta fixa no painel quebrava a cada mudanca de operacao: a
// Argentina saiu da `ebanx` para a `ebanx_ar` em 05/09 e a aba deu 400 ate alguem perceber.
// Pais de contrato continua mandando `gateway` e `country_code`, que vem do proprio pais em
// `getInternationalFeeCountries`: a moeda nao diz o pais (os da zona do euro dividem o EUR).
export const getInternationalFee = async ({ currency, gateway, countryCode } = {}) =>
  AuthenticatedHttp.get('/financial/fee/international/', {
    params: {
      currency,
      ...(gateway ? { gateway } : {}),
      ...(countryCode ? { country_code: countryCode } : {}),
    },
  }).then((response) => response.data);

// Paises com taxa para o produtor, cada um com a moeda, a regiao e a conta que precifica: os da
// EBANX (regiao `america_latina`, CKTPL-1373) e os ativos das contas de contrato.
export const getInternationalFeeCountries = async () =>
  AuthenticatedHttp.get('/financial/fee/international/countries/').then((response) =>
    Array.isArray(response.data?.countries) ? response.data.countries : []
  );

export const updateCompany = async (data) =>
  AuthenticatedHttp.put(`/financial/company/`, { ...data }).then((response) => response.data);

export const addDocumentOnCompany = async ({ files, submitForApproval = false }) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append(`files`, file);
  });

  formData.append('submitForApproval', submitForApproval);

  const { data } = await AuthenticatedHttp.put('/financial/company/add_files/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
};

export const deleteDocumentOnCompany = async (id) =>
  AuthenticatedHttp.delete(`/financial/company/delete_file/${id}/`).then(
    (response) => response.data
  );

export const getWithdrawalFee = async () =>
  AuthenticatedHttp.get(`/financial/withdraw_fee/`).then((response) => response.data);

export const verifyDocumentsService = async () =>
  AuthenticatedHttp.post(`/financial/verification/`).then((response) => response.data);

// to remove
// eslint-disable-next-line prefer-const
let creditCards = [
  {
    id: '1e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'John Doe',
    number: '4111111111111111',
    cvv: '123',
    expirationDate: '12/2025',
    limit: 5000,
    type: 'physical',
    name: 'Visa Gold',
    transactions: [
      {
        id: '2e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-01-01',
        place: 'Supermarket',
        value: 150.75,
      },
      {
        id: '3e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-01-05',
        place: 'Gas Station',
        value: 50.0,
      },
    ],
  },
  {
    id: '4e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Jane Smith',
    number: '5500000000000004',
    cvv: '456',
    expirationDate: '11/2026',
    limit: 7000,
    type: 'virtual',
    name: 'MasterCard Platinum',
    transactions: [
      {
        id: '5e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-02-10',
        place: 'Restaurant',
        value: 200.0,
      },
      {
        id: '6e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-02-15',
        place: 'Online Store',
        value: 120.5,
      },
    ],
  },
  {
    id: '7e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Alice Johnson',
    number: '6011000000000004',
    cvv: '789',
    expirationDate: '10/2027',
    limit: 6000,
    type: 'physical',
    name: 'Discover It',
    transactions: [
      {
        id: '8e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-03-01',
        place: 'Bookstore',
        value: 75.25,
      },
      {
        id: '9e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-03-05',
        place: 'Coffee Shop',
        value: 15.0,
      },
    ],
  },
  {
    id: '10e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Bob Brown',
    number: '30000000000004',
    cvv: '012',
    expirationDate: '09/2025',
    limit: 4000,
    type: 'virtual',
    name: 'Diners Club International',
    transactions: [
      {
        id: '11e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-04-01',
        place: 'Hotel',
        value: 300.0,
      },
      {
        id: '12e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-04-10',
        place: 'Airline',
        value: 500.0,
      },
    ],
  },
  {
    id: '13e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Charlie Davis',
    number: '3530111333300000',
    cvv: '345',
    expirationDate: '08/2026',
    limit: 8000,
    type: 'physical',
    name: 'JCB Standard',
    transactions: [
      {
        id: '14e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-05-01',
        place: 'Gym',
        value: 100.0,
      },
      {
        id: '15e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-05-05',
        place: 'Pharmacy',
        value: 30.0,
      },
    ],
  },
  {
    id: '16e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Diana Evans',
    number: '4000000000000002',
    cvv: '678',
    expirationDate: '07/2025',
    limit: 9000,
    type: 'virtual',
    name: 'Visa Classic',
    transactions: [
      {
        id: '17e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-06-01',
        place: 'Supermarket',
        value: 150.0,
      },
      {
        id: '18e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-06-10',
        place: 'Gas Station',
        value: 60.0,
      },
    ],
  },
  {
    id: '19e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Ethan Foster',
    number: '5105105105105100',
    cvv: '901',
    expirationDate: '06/2026',
    limit: 10000,
    type: 'physical',
    name: 'MasterCard World',
    transactions: [
      {
        id: '20e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-07-01',
        place: 'Restaurant',
        value: 250.0,
      },
      {
        id: '21e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-07-05',
        place: 'Online Store',
        value: 130.0,
      },
    ],
  },
  {
    id: '22e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Fiona Green',
    number: '6011111111111117',
    cvv: '234',
    expirationDate: '05/2026',
    limit: 11000,
    type: 'virtual',
    name: 'Discover More',
    transactions: [
      {
        id: '23e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-08-01',
        place: 'Bookstore',
        value: 80.0,
      },
      {
        id: '24e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-08-05',
        place: 'Coffee Shop',
        value: 20.0,
      },
    ],
  },
  {
    id: '25e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'George Harris',
    number: '30569309025904',
    cvv: '567',
    expirationDate: '04/2025',
    limit: 12000,
    type: 'physical',
    name: 'Diners Club Premier',
    transactions: [
      {
        id: '26e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-09-01',
        place: 'Hotel',
        value: 350.0,
      },
      {
        id: '27e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-09-10',
        place: 'Airline',
        value: 550.0,
      },
    ],
  },
  {
    id: '28e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
    holder: 'Hannah Irving',
    number: '3566002020360505',
    cvv: '890',
    expirationDate: '03/2026',
    limit: 13000,
    type: 'virtual',
    name: 'JCB Gold',
    transactions: [
      {
        id: '29e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-10-01',
        place: 'Gym',
        value: 120.0,
      },
      {
        id: '30e7d1f4e-8f3b-4a2e-9b1e-1a2b3c4d5e6f',
        date: '2023-10-05',
        place: 'Pharmacy',
        value: 40.0,
      },
    ],
  },
];

export const getCreditCards = async () => {
  await delay(1000);

  return Promise.resolve(creditCards);
};

export const getCreditCard = async (id) => {
  await delay(500);
  const creditCard = creditCards.find((card) => card.id === id);
  return Promise.resolve(creditCard);
};

export const createCreditCardService = async (creditCard) => {
  await delay(500);
  creditCard.id = `test-id-${creditCards.length + 1}`;
  creditCard.transactions = [];
  creditCards.push(creditCard);
  return Promise.resolve(creditCard);
};

export const updateCreditCardService = async (id, updatedData) => {
  await delay(500);
  creditCards = creditCards.map((card) => (card.id === id ? { ...card, ...updatedData } : card));
  return Promise.resolve(creditCards.find((card) => card.id === id));
};

export const deleteCreditCardService = async (id) => {
  await delay(500);
  creditCards = creditCards.filter((card) => card.id !== id);
  return Promise.resolve(id);
};

export const getSubscriptions = async ({ countryId } = {}) => {
  const { data } = await AuthenticatedHttp.get('/financial/plan_fee/list_plans/', {
    params: countryId ? { country_id: countryId } : undefined,
  });

  const plans = Array.isArray(data) ? data : data?.plans || data?.results || [];

  const subscriptions = plans.map((plan) => ({
    id: String(plan.id),
    title: plan.name,
    transactionFee: plan.creditCardPercentage ?? 0,
    settlementPeriod: plan.creditCardRelease ?? 0,
    securityReserve: plan.creditCardReserve ?? 0,
    cardAntiFraudTransactionFee: plan.creditCardFixed ?? 0,
    isRecomended: plan.name?.toLowerCase().includes('recomendado') || false,
  }));

  return subscriptions;
};

export const getActiveSubscription = async () => {
  const { data } = await AuthenticatedHttp.get('/financial/plan_fee/current_plan/');

  return data.id;
};

export const postSubscription = async (planType) => {
  const { data } = await AuthenticatedHttp.post('/financial/plan_fee/choose_plan/', {
    plan_type: planType,
  });
  return data;
};

let pixKeys = [
  {
    id: '1',
    type: 'cpf',
    value: '12084668018',
    status: 'registered',
    createdAt: '2025-01-02T00:00:00Z',
  },
  {
    id: '2',
    type: 'cnpj',
    value: '63529559000123',
    status: 'registered',
    createdAt: '2025-01-01T00:00:00Z',
  },
];

export const getFinancialPixKeys = async () => {
  await delay(500);

  return Promise.resolve(pixKeys);
};

export const deletePixKeyService = async (id) => {
  await delay(500);
  pixKeys = pixKeys.filter((pixKey) => pixKey.id !== id);
  return Promise.resolve(id);
};

export const createPixKeyService = async (pixKey) => {
  await delay(500);
  pixKeys.id = `test-id-${pixKeys.length + 1}`;
  pixKeys.push(pixKey);
  return Promise.resolve(pixKey);
};

const billetData = {
  code: '34191.09008 63571.277308 71444.640008 3 85350000000000',
  expirationDate: '2025-10-01T00:00:00Z',
  value: 100.0,
  issuer: 'Banco do Brasil',
  payerName: 'Fulano de Tal',
};

export const getBilletCodeService = async (file) => {
  await delay(500);

  return Promise.resolve(billetData.code);
};

export const readBilletDataService = async (code) => {
  await delay(500);

  return Promise.resolve(billetData);
};

export const getCAFOnboardingLink = async () => {
  const response = await AuthenticatedHttp.post('/financial/onboarding/', {
    onboarding_type: 'onboarding_link',
  });
  return response.data?.onboarding_profile?.provider_fields?.url || null;
};

/**
 * Reativa self-service quem ficou preso no KYC legado (IdWall), sem depender do
 * suporte. Só funciona para quem ainda não tem CNPJ nem conta Celcoin — quem já
 * avançou mais que isso recebe 400 e continua precisando do suporte humano.
 */
export const retryLegacyKyc = async () => {
  const response = await AuthenticatedHttp.post('/financial/kyc/retry_legacy/');
  return response.data;
};

/**
 * Dados jurídicos do vendedor: CNPJ, razão social, nome fantasia, CNAE e endereço.
 *
 * O backend busca na melhor fonte disponível e informa em `origem` qual delas
 * respondeu. Nunca falha por falta de dado — sem nada em lugar nenhum devolve
 * todos os campos nulos e `origem: null`.
 *
 * Null em sessão impersonada: a view tem BlockImpersonateActions, então sem o
 * guard a chamada só geraria um 403 previsível.
 */
export const getCompanyLegalEntity = async (options = {}) => {
  if (isImpersonatingSession()) return null;

  const response = await AuthenticatedHttp.get('/financial/company/legal-entity/', options);

  return response.data;
};
